import {
  Button,
  ButtonIcon,
  ButtonMenu,
  FileUploadWithModal,
  UploadHistoryTableModal,
  UploadMediaModal,
} from '@/components-v2'
import {MAIN_COLUMNS, SUB_COLUMNS} from '@/containers/Promotions/PromotionList/PromotionListConstants.jsx'
import usePromotionUploadHistory from '@/hooks/usePromotionUploadHistory'
import {Constants} from '@/utils/constants/commonConstants'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'

const PROMOTION_ENTITY_TYPE_OPTIONS = [{id: 'promotion', displayText: 'Promotion'}]

/**
 * Custom toolbar header rendered above the v2 Promotion List DataTable.
 * Owns the upload/download actions menu and its related modals.
 * @param {{
 *   resetFilterButtonOptions?: {onFiltersReset?: () => void, text?: string},
 *   canDownload: boolean,
 *   isDownloading: boolean,
 *   onDownloadClick: () => void,
 *   isSelectAll: boolean,
 *   rowSelected: any[],
 *   downloadPromoTemplate: () => void,
 * }} props
 */
export const PromotionListHeader = ({
  resetFilterButtonOptions,
  canDownload,
  isDownloading,
  onDownloadClick,
  isSelectAll,
  rowSelected,
  downloadPromoTemplate,
}) => {
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  // ButtonMenu is a single-select control - remount it after each pick so its internal
  // "selected" state (and the checkmark it renders) doesn't stick to the last clicked action.
  const [actionsMenuKey, setActionsMenuKey] = useState(0)
  const [isMultipleImagesModal, toggleMultipleImagesModal] = useState(false)
  const [promotionListUpload, togglePromotionListUpload] = useState(false)
  const navigate = useNavigate()

  const {uploadHistoryData, downloadUploadError, downloadPromotionFile, getPromotionFileUploadErrors} =
    usePromotionUploadHistory(openUploadHistory)

  const uploadHistoryColumns = MAIN_COLUMNS(downloadPromotionFile, downloadUploadError)

  // Handler for "New Promotion" button click, navigates to the promotion creation page
  const onNewPromotionClick = useCallback(() => {
    navigate(ROUTES.SELECT_PROMOTION_TYPE)
  }, [navigate])

  const actionsMenuItems = useMemo(
    () => [
      {
        id: 'new-promotion',
        label: 'New Promotion',
        icon: 'add_box',
        onClick: () => onNewPromotionClick(),
        disabled: false,
      },
      {
        id: 'upload',
        label: 'Upload Bulk Promotions',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          togglePromotionListUpload(true)
        },
      },
      {
        id: 'upload_images',
        label: 'Upload Images',
        onClick: () => {
          toggleMultipleImagesModal(true)
        },
      },
      {
        id: 'upload_history',
        label: 'View Upload History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        id: 'download',
        label: 'Download',
        onClick: () => {
          onDownloadClick()
        },
        disabled: !canDownload || isDownloading, // Dynamic disable based on selection
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      toggleMultipleImagesModal,
      setOpenUploadHistory,
      isSelectAll,
      rowSelected,
      onNewPromotionClick,
      canDownload,
      isDownloading,
      onDownloadClick,
    ]
  )

  // ButtonMenu only reports the picked option via onChange - it never calls the option's own onClick.
  const handleActionsMenuChange = value => {
    const selectedAction = actionsMenuItems.find(action => action.id === value?.id)

    if (!selectedAction || selectedAction.disabled) {
      setActionsMenuKey(prevKey => prevKey + 1)
      return
    }

    selectedAction.onClick?.()
    setActionsMenuKey(prevKey => prevKey + 1)
  }

  return (
    <>
      <div className="datatable-custom-header">
        <Button
          variant="tertiary"
          size="medium"
          text={resetFilterButtonOptions?.text ?? 'Reset All'}
          onClick={resetFilterButtonOptions?.onFiltersReset}
        />
        <div className="flex gap-2">
          <ButtonIcon
            icon="download"
            type="default"
            label="Download"
            disabled={!canDownload || isDownloading}
            onClick={onDownloadClick}
          />
          <ButtonMenu
            key={actionsMenuKey}
            className={
              !canDownload || isDownloading ? 'promotion-actions-menu is-download-disabled' : 'promotion-actions-menu'
            }
            options={actionsMenuItems}
            onChange={handleActionsMenuChange}
            height={actionsMenuItems.length * 44 + 16}
            width="280px"
            buttonProps={{
              variant: 'tertiary',
              size: 'medium',
              iconLeading: 'more_vert',
              title: 'More actions',
              'aria-label': 'More actions',
            }}
            menuPosition="bottom-left"
          />
        </div>
      </div>
      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData?.data || []}
        totalRecords={uploadHistoryData?.totalRecords}
        mainColumns={uploadHistoryColumns}
        subColumns={SUB_COLUMNS}
        fetchSubRows={getPromotionFileUploadErrors}
        isDark={true}
        data-testid="upload-history-modal"
      />
      <UploadMediaModal
        // Remount on every open/close instead of manually resetting internal state on close.
        key={`upload-media-${isMultipleImagesModal}`}
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        entityTypeOptions={PROMOTION_ENTITY_TYPE_OPTIONS}
      />
      <FileUploadWithModal
        key={`bulk-upload-promotions-${promotionListUpload}`}
        openUpload={promotionListUpload}
        setOpenUpload={togglePromotionListUpload}
        title="Bulk Upload Promotions"
        uploadKind="bulkPromotion"
        label={'Upload Bulk Promotion file'}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={downloadPromoTemplate}
        acceptedExtensions={Constants.Xlsx}
      />
    </>
  )
}
